<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-4 mt-5">
      <div class="d-flex align-items-center">
        <h2>Directors</h2>
        <button @click="addDirector" class="btn btn-success ml-4">
          Add Director
        </button>
      </div>
      <div style="width: 50%;">
        <SearchBox
          v-model="searchTerm"
          placeholder="Search directors..."
        />
      </div>
    </div>
    <AIVTable
      v-if="filteredDirectors && filteredDirectors.length"
      :columns="directorColumns"
      :data="filteredDirectors"
      :pagination="{ total: 0, totalPages: 0, currentPage: 0, perPage: 0 }"
    >
      <template #cell-dates="{ row }">
        <div class="dates-column">
          <div class="created-date">Created: {{ formatDate(row.createdAt) }}</div>
          <div class="updated-date">Updated: {{ formatDate(row.updatedAt) }}</div>
        </div>
      </template>
      <template #cell-actions="{ row }">
        <button @click="editDirector(row)" class="btn btn-primary btn-sm mr-2">
          Edit
        </button>
        <button @click="removeDirector(row)" class="btn btn-danger btn-sm">
          Delete
        </button>
      </template>
    </AIVTable>

    <!-- Director Modal -->
    <AIVDialog
      :visible="$modal.state.directorModal"
      :title="modalTitle"
      :show-footer="true"
      :custom-class="'director-modal'"
      @update:visible="$modal.close('directorModal')"
    >
      <!-- Edit/Create Mode -->
      <div v-if="modal.mode === 'edit' || modal.mode === 'create'" class="director-form">
        <div class="form-group mb-3">
          <label for="directorName" class="form-label">Director Name *</label>
          <input
            id="directorName"
            v-model="modal.formData.name"
            type="text"
            class="form-control"
            placeholder="Enter director name"
            required
          />
        </div>
        <div class="form-group mb-3">
          <label for="googleDriveLocation" class="form-label">Google Drive Location</label>
          <input
            id="googleDriveLocation"
            v-model="modal.formData.google_drive_location"
            type="text"
            class="form-control"
            placeholder="Enter Google Drive location URL"
          />
        </div>
      </div>

      <!-- Remove Mode -->
      <div v-if="modal.mode === 'remove'" class="director-remove-confirm">
        <p class="text-center mb-4">
          Are you sure you want to delete the director 
          <strong>"{{ modal.selectedDirector?.name }}"</strong>?
        </p>
        <p class="text-muted text-center">This action cannot be undone.</p>
      </div>

      <!-- Modal Footer -->
      <template #footer>
        <div class="d-flex justify-content-around">
          <button 
            @click="closeModal" 
            class="btn btn-secondary"
          >
            Cancel
          </button>
          <button 
            v-if="modal.mode === 'edit'"
            @click="saveDirector" 
            class="btn btn-primary ml-2"
            :disabled="modal.loading || !modal.formData.name"
          >
            {{ modal.loading ? 'Saving...' : 'Save' }}
          </button>
          <button 
            v-if="modal.mode === 'create'"
            @click="createDirector" 
            class="btn btn-success ml-2"
            :disabled="modal.loading || !modal.formData.name"
          >
            {{ modal.loading ? 'Creating...' : 'Create Director' }}
          </button>
          <button 
            v-if="modal.mode === 'remove'"
            @click="confirmRemoval" 
            class="btn btn-danger ml-2"
            :disabled="modal.loading"
          >
            {{ modal.loading ? 'Deleting...' : 'Delete' }}
          </button>
        </div>
      </template>
    </AIVDialog>

  </div>
</template>

<script>
import DirectorsAPIService from "@/serviceHandlers/DirectorsAPIservice";
import AIVTable from "@/components/AIVTable.vue";
import AIVDialog from "@/components/AIVDialog.vue";
import SearchBox from "@/components/SearchBox.vue";

const directorsAPIService = new DirectorsAPIService()

export default {
  name: "DirectorsPage",
  components: {
    AIVTable,
    AIVDialog,
    SearchBox
  },
  data(){
    return {
      searchTerm: '',
      directors: [],
      directorColumns: [
        { key: 'name', label: 'Name' },
        { key: 'google_drive_location', label: 'Google Drive Location' },
        { key: 'dates', label: 'Created / Updated', component: 'dates' },
        { 
          key: 'actions', 
          label: 'Actions', 
          sortable: false,
          component: 'div'
        }
      ],
      modal: {
        mode: null, // 'edit', 'remove', or 'create'
        selectedDirector: null,
        loading: false,
        formData: {
          name: '',
          google_drive_location: ''
        }
      }
    }
  },
  computed: {
    filteredDirectors() {
      if (!this.directors || !this.searchTerm) {
        return this.directors || [];
      }
      
      const searchLower = this.searchTerm.toLowerCase();
      return this.directors.filter(director => {
        const matchesName = director.name?.toLowerCase().includes(searchLower);
        const matchesDriveLocation = director.google_drive_location?.toLowerCase().includes(searchLower);
        
        return matchesName || matchesDriveLocation;
      });
    },
    modalTitle() {
      if (this.modal.mode === 'edit') {
        return 'Edit Director';
      } else if (this.modal.mode === 'remove') {
        return 'Delete Director';
      } else if (this.modal.mode === 'create') {
        return 'Create New Director';
      }
      return '';
    }
  },
  async mounted(){
    console.clear()
    await this.getDirectors()
  },
  methods: {
    async getDirectors(){
      try {
        this.directors = await directorsAPIService.getDirectors(this.$router)
        console.log(this.directors)
      } catch (e) {
        console.error(e)
      }
    },
    editDirector(director) {
      console.log(director)
      this.modal.selectedDirector = director;
      this.modal.mode = 'edit';
      this.modal.formData.name = director.name;
      this.modal.formData.google_drive_location = director.google_drive_location || '';
      this.$modal.open('directorModal');
    },
    removeDirector(director) {
      this.modal.selectedDirector = director;
      this.modal.mode = 'remove';
      this.$modal.open('directorModal');
    },
    addDirector() {
      this.modal.mode = 'create';
      this.modal.selectedDirector = null;
      this.modal.formData.name = '';
      this.modal.formData.google_drive_location = '';
      this.$modal.open('directorModal');
    },
    closeModal() {
      this.$modal.close('directorModal');
      this.modal.mode = null;
      this.modal.selectedDirector = null;
      this.modal.loading = false;
      this.modal.formData.name = '';
      this.modal.formData.google_drive_location = '';
    },
    async saveDirector() {
      this.modal.loading = true;
      try {
        const directorData = {
          name: this.modal.formData.name,
          google_drive_location: this.modal.formData.google_drive_location
        };
        
        await directorsAPIService.updateDirector(
          this.modal.selectedDirector.id,
          directorData,
          this.$router
        );
        
        // Refresh the directors data to show updated info
        await this.getDirectors();
        this.closeModal();
        
        console.log('Director updated successfully');
      } catch (error) {
        console.error('Error updating director:', error);
        alert('Failed to update director. Please try again.');
      } finally {
        this.modal.loading = false;
      }
    },
    async createDirector() {
      this.modal.loading = true;
      try {
        const directorData = {
          name: this.modal.formData.name,
          google_drive_location: this.modal.formData.google_drive_location
        };
        
        await directorsAPIService.createDirector(
          directorData,
          this.$router
        );
        
        // Refresh the directors data to show the new director
        await this.getDirectors();
        this.closeModal();
        
        console.log('Director created successfully');
      } catch (error) {
        console.error('Error creating director:', error);
        alert('Failed to create director. Please try again.');
      } finally {
        this.modal.loading = false;
      }
    },
    async confirmRemoval() {
      this.modal.loading = true;
      try {
        await directorsAPIService.deleteDirector(
          this.modal.selectedDirector.id,
          this.$router
        );
        
        // Refresh the directors data to remove the director from the list
        await this.getDirectors();
        this.closeModal();
        
        console.log('Director deleted successfully');
      } catch (error) {
        console.error('Error deleting director:', error);
        alert('Failed to delete director. Please try again.');
      } finally {
        this.modal.loading = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  }
};
</script>

<style scoped>
.dates-column {
  font-size: 0.875rem;
}

.created-date {
  margin-bottom: 2px;
}

.updated-date {
  color: #6c757d;
  font-size: 0.8rem;
}
</style>